import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Mail, Phone, Globe, Star, ChevronRight, ChevronLeft } from 'lucide-react';

const AZDirectory = () => {
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedAffiliates, setGroupedAffiliates] = useState({});
  const [featuredAffiliates, setFeaturedAffiliates] = useState([]);
  const [letters, setLetters] = useState([]);
  const letterRefs = useRef({});

  // Create axios instance
  const api = axios.create({
    baseURL: 'https://backend.dlapakistan.com/api/dla-trade',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Fetch affiliates from backend
  const fetchAffiliates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/affiliate-members/');
      const affiliatesData = response.data;
      setAffiliates(affiliatesData);
      
      // Get featured affiliates
      const featured = affiliatesData.filter(affiliate => affiliate.featured);
      setFeaturedAffiliates(featured.slice(0, 3)); // Limit to 3 featured
      
      // Group affiliates by first letter
      const grouped = affiliatesData.reduce((groups, affiliate) => {
        const firstLetter = affiliate.title?.charAt(0).toUpperCase() || 'A';
        if (!groups[firstLetter]) {
          groups[firstLetter] = [];
        }
        groups[firstLetter].push(affiliate);
        return groups;
      }, {});
      
      setGroupedAffiliates(grouped);
      
      // Get sorted letters
      const sortedLetters = Object.keys(grouped).sort();
      setLetters(sortedLetters);
      
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      console.error('Full error details:', error.response);
    } finally {
      setLoading(false);
    }
  };

  // Load affiliates on mount
  useEffect(() => {
    fetchAffiliates();
  }, []);

  // Generate all letters A-Z
  const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Scroll to letter section
  const scrollToLetter = (letter) => {
    setSelectedLetter(letter);
    if (letterRefs.current[letter]) {
      const topOffset = 100; // Offset for fixed header
      const elementPosition = letterRefs.current[letter].offsetTop;
      const offsetPosition = elementPosition - topOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Handle company click - show details
  const handleCompanyClick = (companyName) => {
    const affiliate = affiliates.find(a => a.title === companyName);
    if (affiliate) {
      setSelectedAffiliate(affiliate);
      scrollToTop();
    }
  };

  // Navigate to previous/next affiliate
  const navigateAffiliate = (direction) => {
    if (!selectedAffiliate) return;
    
    const currentIndex = affiliates.findIndex(a => a.id === selectedAffiliate.id);
    let targetIndex;
    
    if (direction === 'prev') {
      targetIndex = currentIndex > 0 ? currentIndex - 1 : affiliates.length - 1;
    } else {
      targetIndex = currentIndex < affiliates.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSelectedAffiliate(affiliates[targetIndex]);
    scrollToTop();
  };

  // Handle back from detail view
  const handleBack = () => {
    setSelectedAffiliate(null);
    setSelectedLetter(null);
    scrollToTop();
  };

  // --- DETAIL VIEW ---
  if (selectedAffiliate) {
    const data = selectedAffiliate;
    return (
      <div className="max-w-5xl mx-auto p-6 font-sans text-[#333333]">
        {/* Simple Back button */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="text-[black] hover:text-green-700 transition-colors duration-200 flex items-left gap-1 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        </div>

        {/* Clean Left-Aligned Detail View */}
        <div className="max-w-2xl">
          <h1 className="text-[32px] font-normal mb-2 text-[#3c4858]">{data.title}</h1>
          <hr className="border-t border-gray-200 mb-6 w-full" />

          {/* Logo/Image Section - Simple */}
          <div className="mb-6">
            <div className="w-full max-w-[300px] h-[150px] bg-gray-50 rounded-sm border border-gray-200 flex items-center justify-center p-3 mb-4">
              {data.image_url ? (
                <img
                  src={data.image_url}
                  alt={data.title}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-2 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-gray-500">
                      {data.title?.charAt(0) || 'A'}
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs">No Logo Available</p>
                </div>
              )}
            </div>
          </div>

          {/* Contact Details - Clean Left Aligned */}
          <div className="space-y-4 mb-8">
            <h3 className="text-[18px] font-semibold text-[#3c4858] border-b pb-2">Contact Details</h3>
            
            {/* Website */}
            {data.website && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">Website</p>
                <a
                  href={data.website.startsWith('http') ? data.website : `https://${data.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#70B444] hover:underline text-sm break-all inline-flex items-center gap-1"
                >
                  {data.website}
                  <Globe className="w-3 h-3 ml-1" />
                </a>
              </div>
            )}
            
            {/* Phone */}
            {data.phone && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">Customer Service Number</p>
                <div className="flex items-center gap-2">
                  <span className="text-gray-800 text-sm">{data.phone}</span>
                  <a
                    href={`tel:${data.phone}`}
                    className="text-xs text-[#70B444] hover:text-green-700 border border-[#70B444] px-2 py-0.5 rounded"
                  >
                    Call
                  </a>
                </div>
              </div>
            )}
            
            {/* Email */}
            {data.email && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">Customer Service Email</p>
                <div className="flex items-center gap-2">
                  <a
                    href={`mailto:${data.email}`}
                    className="text-gray-800 hover:text-[#70B444] text-sm break-all"
                  >
                    {data.email}
                  </a>
                  <a
                    href={`mailto:${data.email}`}
                    className="text-xs text-[#70B444] hover:text-green-700 border border-[#70B444] px-2 py-0.5 rounded"
                  >
                    Email
                  </a>
                </div>
              </div>
            )}
            
            {/* Featured Status - Simple */}
            {data.featured && (
              <div className="pt-3 mt-3 border-t border-gray-200">
                <div className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-50 text-yellow-700 rounded text-xs">
                  <Star className="w-3 h-3 fill-yellow-500" />
                  <span className="font-medium">Featured Affiliate</span>
                </div>
              </div>
            )}
          </div>

          {/* Simple Navigation */}
          <div className="flex justify-between items-center border-t border-gray-200 pt-4">
            <button
              onClick={() => navigateAffiliate('prev')}
              className="text-xs text-gray-600 hover:text-[#70B444] flex items-center gap-1 transition-colors"
            >
              <ChevronLeft className="w-3 h-3" />
              Previous
            </button>
            
            <button
              onClick={() => navigateAffiliate('next')}
              className="text-xs text-gray-600 hover:text-[#70B444] flex items-center gap-1 transition-colors"
            >
              Next
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- MAIN DIRECTORY VIEW ---
  return (
    <div className="max-w-7xl mx-auto p-6 font-sans text-[#333333]">
      <h1 className="text-[42px] font-normal mb-8 text-[#3c4858]">A-Z Directory of Affiliate Members</h1>
      <hr className="border-t border-gray-200 mb-10" />

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600 mb-4"></div>
            <p className="text-gray-600">Loading affiliates...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Featured Affiliates Section */}
          {featuredAffiliates.length > 0 && (
            <div className="bg-[#70B444] p-8 rounded-sm mb-12 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-white text-[24px] font-normal">Featured Affiliates</h2>
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                  <span className="text-white text-sm">Featured</span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {featuredAffiliates.map((affiliate, index) => (
                  <div key={affiliate.id} className="bg-white p-4 flex flex-col items-center justify-between min-h-[300px] shadow-md hover:shadow-lg transition-shadow duration-200">
                    {/* Logo/Image */}
                    <div className="w-full h-48 bg-gray-50 flex items-center justify-center p-4">
                      {affiliate.image_url ? (
                        <img
                          src={affiliate.image_url}
                          alt={affiliate.title}
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <div className="text-center">
                          <div className="w-20 h-20 mx-auto mb-3 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-2xl font-bold text-gray-500">
                              {affiliate.title?.charAt(0) || 'A'}
                            </span>
                          </div>
                          <p className="text-gray-400 text-sm">No Logo</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2 text-center px-2">
                      {affiliate.title}
                    </h3>
                    
                    {/* Read More Button */}
                    <button
                      onClick={() => handleCompanyClick(affiliate.title)}
                      className="text-[#70B444] text-sm hover:underline cursor-pointer font-semibold mt-2"
                    >
                      Read More...
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* A-Z Quick Navigation */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Quick Navigation</h3>
            <div className="flex flex-wrap gap-2 justify-center max-w-3xl mx-auto">
              {allLetters.map(letter => (
                <button
                  key={letter}
                  onClick={() => scrollToLetter(letter)}
                  className={`w-10 h-10 rounded-md flex items-center justify-center text-lg font-medium transition-all duration-200 ${
                    letters.includes(letter)
                      ? 'bg-[#70B444] text-white hover:bg-green-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  } ${
                    selectedLetter === letter
                      ? 'ring-2 ring-[#70B444] ring-offset-2'
                      : ''
                  }`}
                  disabled={!letters.includes(letter)}
                  title={letters.includes(letter) ? `Jump to letter ${letter}` : `No affiliates under ${letter}`}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>

          {/* Alphabetical Directory */}
          <div className="max-w-5xl mx-auto space-y-12">
            {letters.map(letter => (
              <div
                key={letter}
                ref={el => letterRefs.current[letter] = el}
              >
                <div className="border-b-[1.5px] border-black/80 pb-2 mb-4">
                  <h2 className="text-[32px] font-normal text-[#3c4858]">{letter}</h2>
                </div>
                <ul className="space-y-4 mb-6">
                  {groupedAffiliates[letter]?.map(affiliate => (
                    <li key={affiliate.id} className="flex items-center gap-2">
                      <button
                        onClick={() => handleCompanyClick(affiliate.title)}
                        className="text-[#70B444] text-[18px] hover:underline text-left flex items-center gap-2"
                      >
                        {affiliate.title}
                        {affiliate.featured && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="flex justify-end w-full pt-2">
                  <button
                    onClick={scrollToTop}
                    className="text-[11px] text-[#70B444] uppercase font-bold hover:text-green-700"
                  >
                    ^ Back to top ^
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {letters.length === 0 && !loading && (
            <div className="text-center py-16">
              <div className="max-w-md mx-auto">
                <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-green-600">A-Z</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Affiliate Members Yet</h3>
                <p className="text-gray-600 mb-6">
                  There are no affiliate members in the directory yet.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AZDirectory;